import type { MessageDescriptor } from 'react-intl';
import type { SalaryInferenceExplanationKind } from './resolve-salary-inference-explanation-kind';

/** Below this many comparable postings, the estimate leans mostly on a broader (less
 *  specific) market slice — say so plainly instead of presenting it with false precision. */
export const MIN_CONFIDENT_SALARY_ESTIMATE_SAMPLE_SIZE = 10;

const MARKET_MEDIAN: MessageDescriptor = {
  id: 'salaryInference.marketMedianFromSimilar',
  defaultMessage:
    'The employer did not state pay in this listing. We show the median minimum–maximum band from similar jobs on JobCrawls, using the most specific slice we have: company + job title + location when possible, otherwise title + location, otherwise seniority, otherwise title alone. Amounts are in EUR per month.',
  description:
    'Tooltip and mobile expand text when salary was filled from aggregated market medians (salaryIsEstimate)',
};

const MARKET_MEDIAN_WITH_COUNT: MessageDescriptor = {
  id: 'salaryInference.marketMedianFromSimilarWithCount',
  defaultMessage:
    'The employer did not state pay in this listing. We show the median minimum–maximum band from similar jobs on JobCrawls, based on {sampleSize} comparable postings, using the most specific slice we have: company + job title + location when possible, otherwise title + location, otherwise seniority, otherwise title alone. Amounts are in EUR per month.',
  description:
    'Tooltip and mobile expand text when salary was filled from aggregated market medians, with the number of comparable postings behind the estimate (salaryIsEstimate, sampleSize >= confidence threshold)',
};

const MARKET_MEDIAN_LOW_CONFIDENCE: MessageDescriptor = {
  id: 'salaryInference.marketMedianFromSimilarLowConfidence',
  defaultMessage:
    'The employer did not state pay in this listing, and we found only {sampleSize} comparable posting(s) for this specific role, so this estimate leans mostly on the broader local market rather than this exact job title. Treat it as a rough guide, not a precise figure. Amounts are in EUR per month.',
  description:
    'Tooltip and mobile expand text when the salary estimate is backed by very few comparable postings (salaryIsEstimate, sampleSize below confidence threshold)',
};

const NUMERIC_WITHOUT_QUOTE: MessageDescriptor = {
  id: 'salaryInference.numericWithoutListingQuote',
  defaultMessage:
    'Our pipeline extracted a pay range for this ad, but we do not have a short verbatim salary quote from the listing text. Treat the numbers as unverified.',
  description:
    'Tooltip and mobile expand when numeric salary exists without salaryDetails quote from the ad',
};

const DESCRIPTORS: Record<
  Exclude<SalaryInferenceExplanationKind, null>,
  MessageDescriptor
> = {
  market_median_from_similar_jobs: MARKET_MEDIAN,
  numeric_range_without_listing_quote: NUMERIC_WITHOUT_QUOTE,
};

export function salaryInferenceExplanationDescriptor(
  kind: SalaryInferenceExplanationKind,
): MessageDescriptor | null {
  if (!kind) return null;
  return DESCRIPTORS[kind];
}

/**
 * `sampleSize` is optional and backward-compatible: omit it (existing SRP hit-card callers)
 * to get the original fixed wording. Pass it (job detail page) to get a message naming how
 * many comparable postings back the estimate, with a distinct low-confidence phrasing below
 * {@link MIN_CONFIDENT_SALARY_ESTIMATE_SAMPLE_SIZE}.
 */
export function formatSalaryInferenceExplanation(
  kind: SalaryInferenceExplanationKind,
  formatMessage: (descriptor: MessageDescriptor, values?: Record<string, string | number>) => string,
  sampleSize?: number | null,
): string {
  if (kind === 'market_median_from_similar_jobs' && typeof sampleSize === 'number' && sampleSize > 0) {
    const descriptor =
      sampleSize < MIN_CONFIDENT_SALARY_ESTIMATE_SAMPLE_SIZE ? MARKET_MEDIAN_LOW_CONFIDENCE : MARKET_MEDIAN_WITH_COUNT;
    return formatMessage(descriptor, { sampleSize });
  }
  const descriptor = salaryInferenceExplanationDescriptor(kind);
  return descriptor ? formatMessage(descriptor) : '';
}
